import { addTileArtwork, clearTileArtwork } from '@/utils/tileArtwork';
import { TileType } from '@/models/TileTypes';
import {
  TileDefinition,
  TileGraphicsDefinition,
  ContextMenuActionFn,
  CreateContextMenuItemsData,
} from '@/models/tiles/TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { pixelRect } from '@/utils/tilePixels';
import { colors } from '@/styles';
import { defaultTileGraphics } from '@/models/tiles/DefaultTileGraphics';
import { PixelArtData, PIXEL_ART_ENABLED } from '@/models/PixelArtData';
import { getOrCreateTexture } from './tileTextureCache';
import * as PIXI from 'pixi.js';

export interface StructureTileState {
  customColor: string | null;
  pixelArtBase64: string | null;
}

export class StructureTile implements TileDefinition<StructureTileState> {
  type = TileType.Structure;
  name = 'Structure';
  canRotate = false;

  createDefaultState = (): StructureTileState => ({
    customColor: null,
    pixelArtBase64: null,
  });

  createContextMenuItems(
    data: CreateContextMenuItemsData<StructureTileState>,
  ): Array<{ label: string; action: ContextMenuActionFn }> {
    if (!PIXEL_ART_ENABLED) return [];
    const { instance } = data;
    return [
      {
        label: 'Edit Appearance',
        action: (_takeInput, uiModel) => {
          const state = instance.state;
          const pixelArt = state.pixelArtBase64
            ? PixelArtData.fromBase64(state.pixelArtBase64)
            : null;
          uiModel.openStructurePixelArtDialog({
            color: state.customColor ?? colors.component,
            pixelArt,
            onSave: (result) => {
              instance.state = {
                ...instance.state,
                customColor: result.color !== colors.component ? result.color : null,
                pixelArtBase64: result.pixelArt ? result.pixelArt.toBase64() : null,
              };
              instance.renderVersion++;
            },
          });
        },
      },
    ];
  }

  ioSides = {
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.LEFT]: { type: TileSideIOType.NONE, forceConnection: false },
  };

  graphics = structureTileGraphics;
}

export const structureTileGraphics: TileGraphicsDefinition<StructureTileState> = {
  type: TileType.Structure,
  draw: (graphics, data) => {
    defaultTileGraphics(graphics);

    const state = data?.state;
    const pixelArtB64 = state?.pixelArtBase64;

    if (pixelArtB64) {
      const tex = getOrCreateTexture(`struct:${pixelArtB64}`, () =>
        PixelArtData.fromBase64(pixelArtB64).toDataUrlSync(4),
      );
      if (tex) {
        // Clear children from previous draw
        clearTileArtwork(graphics);
        const sprite = new PIXI.Sprite(tex);
        sprite.x = 5;
        sprite.y = 5;
        sprite.width = 110;
        sprite.height = 110;
        addTileArtwork(graphics, sprite);
        return;
      }
    }

    const fillColor = state?.customColor ?? colors.component;
    const borderColor = colors.componentSecondary;

    // The body reaches the cell boundary; use a one-pixel border.
    graphics.rect(0, 0, 120, 120);
    graphics.fill(borderColor);

    pixelRect(graphics, 1, 1, 23, 23, fillColor);
  },
};
